import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useMerchant } from '../../contexts/MerchantContext';
import LineLoginButton from './LineLoginButton';

const LoginPage = ({ merchantSlug: propMerchantSlug = null }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { merchantSlug: paramMerchantSlug } = useParams();
  const { isAuthenticated, loading } = useAuth();
  const { merchant } = useMerchant();
  const [error, setError] = useState('');
  const [debugInfo, setDebugInfo] = useState({});
  
  // Use merchant slug from URL params if available, otherwise use prop
  const merchantSlug = paramMerchantSlug || propMerchantSlug;

  useEffect(() => {
    // If user is already authenticated, redirect to profile
    if (isAuthenticated && !loading) {
      const redirectTo = sessionStorage.getItem('auth_redirect') || 
        (merchantSlug ? `/${merchantSlug}/profile` : '/profile');
      sessionStorage.removeItem('auth_redirect');
      navigate(redirectTo);
    }
  }, [isAuthenticated, loading, navigate, merchantSlug]);

  useEffect(() => {
    // Handle error messages from URL parameters
    const errorParam = searchParams.get('error');
    if (errorParam) {
      switch (errorParam) {
        case 'line_auth_failed':
          setError('LINE 登入失敗，請重試');
          break;
        case 'data_parse_failed':
          setError('處理登入資料時發生錯誤');
          break;
        case 'no_data':
          setError('未收到有效的認證資料');
          break;
        case 'callback_processing_failed':
          setError('處理登入回調時發生錯誤');
          break;
        case 'auth_required':
          setError('請先登入以繼續');
          break;
        default:
          setError('登入過程中發生錯誤');
      }
    }
  }, [searchParams]);

  useEffect(() => {
    // Collect debug information for LINE integration
    const debugData = {
      // URL Parameters
      urlParams: Object.fromEntries(searchParams.entries()),
      
      // Merchant Context
      merchantSlug: merchantSlug,
      merchantData: merchant ? {
        id: merchant.id,
        name: merchant.name,
        slug: merchant.slug,
        description: merchant.description
      } : null,
      
      // Environment Variables (LINE Config)
      lineConfig: {
        clientId: process.env.REACT_APP_LINE_CLIENT_ID ? 'Set' : 'Not Set',
        apiUrl: process.env.REACT_APP_API_URL || 'Default (192.168.1.112:8040)',
      },
      
      // Current Route Info
      currentPath: window.location.pathname,
      currentSearch: window.location.search,
      currentHash: window.location.hash,
      
      // Session Storage
      sessionData: {
        authRedirect: sessionStorage.getItem('auth_redirect'),
      },
      
      // Auth Context
      authState: {
        isAuthenticated: isAuthenticated,
        loading: loading,
      },
      
      // Timestamp
      timestamp: new Date().toISOString(),
    };
    
    setDebugInfo(debugData);
    
    // Also log to console for easier debugging
    console.log('🔍 LINE Login Debug Info:', debugData);
  }, [searchParams, merchantSlug, merchant, isAuthenticated, loading]);

  const handleLineLoginError = (error) => {
    console.error('LINE login error:', error);
    setError(error.message || '登入失敗，請重試');
  };

  const clearError = () => {
    setError('');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">載入中...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="bg-white shadow-md rounded-lg p-8">
          {/* Header */}
          <div className="text-center">
            {merchant ? (
              <>
                <h2 className="text-3xl font-bold text-gray-900 mb-2">
                  歡迎來到 {merchant.name}
                </h2>
                <p className="text-gray-600 mb-8">
                  使用您的 LINE 帳號登入以開始體驗服務
                </p>
              </>
            ) : (
              <>
                <h2 className="text-3xl font-bold text-gray-900 mb-2">
                  歡迎登入
                </h2>
                <p className="text-gray-600 mb-8">
                  使用您的 LINE 帳號快速登入
                </p>
              </>
            )}
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg 
                    className="h-5 w-5 text-red-400" 
                    viewBox="0 0 20 20" 
                    fill="currentColor"
                  >
                    <path 
                      fillRule="evenodd" 
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" 
                      clipRule="evenodd" 
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-800">{error}</p>
                </div>
                <div className="ml-auto pl-3">
                  <button
                    onClick={clearError}
                    className="inline-flex text-red-400 hover:text-red-600"
                  >
                    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path 
                        fillRule="evenodd" 
                        d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" 
                        clipRule="evenodd" 
                      />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Debug Information - LINE Integration Status */}
          {process.env.NODE_ENV === 'development' || window.location.search.includes('debug=true') ? (
            <div className="mb-6 bg-blue-50 border border-blue-200 rounded-md p-4">
              <div className="flex items-center mb-2">
                <svg className="h-5 w-5 text-blue-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
                <h3 className="text-sm font-medium text-blue-800">LINE 整合調試資訊</h3>
              </div>
              <div className="text-xs text-blue-700 space-y-2">
                <div>
                  <strong>商戶資訊:</strong> {debugInfo.merchantSlug || 'N/A'}
                  {debugInfo.merchantData && (
                    <span className="ml-2">({debugInfo.merchantData.name})</span>
                  )}
                </div>
                <div>
                  <strong>LINE 配置:</strong> 
                  <span className={`ml-2 px-2 py-1 rounded text-xs ${
                    debugInfo.lineConfig?.clientId === 'Set' 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    Client ID: {debugInfo.lineConfig?.clientId || 'Not Set'}
                  </span>
                </div>
                <div>
                  <strong>API URL:</strong> {debugInfo.lineConfig?.apiUrl}
                </div>
                <div>
                  <strong>當前路徑:</strong> {debugInfo.currentPath}
                </div>
                {Object.keys(debugInfo.urlParams || {}).length > 0 && (
                  <div>
                    <strong>URL 參數:</strong>
                    <pre className="mt-1 text-xs bg-blue-100 p-2 rounded overflow-x-auto">
                      {JSON.stringify(debugInfo.urlParams, null, 2)}
                    </pre>
                  </div>
                )}
                <div>
                  <strong>認證狀態:</strong> 
                  <span className={`ml-2 px-2 py-1 rounded text-xs ${
                    debugInfo.authState?.isAuthenticated 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {debugInfo.authState?.isAuthenticated ? '已登入' : '未登入'}
                  </span>
                </div>
                <div className="text-xs text-blue-600">
                  更新時間: {debugInfo.timestamp}
                </div>
              </div>
            </div>
          ) : null}

          {/* Login Options */}
          <div className="space-y-4">
            <LineLoginButton
              merchantSlug={merchantSlug}
              size="large"
              className="w-full"
              onError={handleLineLoginError}
            />
          </div>

          {/* Additional Information */}
          <div className="mt-8 text-center">
            <p className="text-sm text-gray-500">
              登入即表示您同意我們的
              <a href="/terms" className="text-blue-600 hover:text-blue-500 ml-1">
                服務條款
              </a>
              和
              <a href="/privacy" className="text-blue-600 hover:text-blue-500 ml-1">
                隱私政策
              </a>
            </p>
          </div>

          {/* Guest Option */}
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600 mb-3">
              不想登入？
            </p>
            <button
              onClick={() => navigate(merchantSlug ? `/${merchantSlug}` : '/')}
              className="text-blue-600 hover:text-blue-500 text-sm font-medium"
            >
              {merchantSlug ? '繼續瀏覽服務' : '繼續以訪客身份瀏覽'}
            </button>
          </div>
        </div>

        {/* Features */}
        <div className="bg-white shadow-md rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            登入後您可以：
          </h3>
          <ul className="space-y-2 text-sm text-gray-600">
            <li className="flex items-center">
              <svg className="h-4 w-4 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              查看所有體驗記錄
            </li>
            <li className="flex items-center">
              <svg className="h-4 w-4 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              快速填入個人資訊
            </li>
            <li className="flex items-center">
              <svg className="h-4 w-4 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              管理多個商戶的會員資格
            </li>
            <li className="flex items-center">
              <svg className="h-4 w-4 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              查看支付記錄
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;